import type { FeedbackCloseReason, MyFeedbackItem } from './apiClient';

/**
 * The rules behind "My feedback", kept out of the components so each one is testable on its own.
 */

// ─── The link in the emails ─────────────────────────────────────────────────────

/**
 * ⚠️ THE EMAILS LINK TO `<site>/?feedback=mine`, and this is the screen's half of that agreement. The other half is
 * `FEEDBACK_LINK_PATH` in the API's `feedback-notices.ts`, and `feedbackLink.spec.ts` fails if the two stop saying the same
 * thing: change one alone and every email's link silently stops opening the drawer.
 */
export const FEEDBACK_LINK_PARAM = 'feedback';
export const FEEDBACK_LINK_VALUE = 'mine';

/** Where the link is remembered across the sign-in screen: this tab only, and gone when it closes. */
const STASH_KEY = 'pt-open-my-feedback';

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** The tab's storage, or `undefined` where it cannot be had (a private window, blocked site data). It can throw on access. */
function tabStorage(): Store | undefined {
  try {
    return typeof window === 'undefined' ? undefined : window.sessionStorage;
  } catch {
    return undefined;
  }
}

/** Storage can fail on any call, and nothing here is worth failing over: the worst case is a drawer that does not open itself. */
function attempt<T>(fn: () => T, otherwise: T): T {
  try {
    return fn();
  } catch {
    return otherwise;
  }
}

/** Does this query string ask for My feedback? */
export function hasFeedbackLink(search: string): boolean {
  return new URLSearchParams(search).get(FEEDBACK_LINK_PARAM) === FEEDBACK_LINK_VALUE;
}

/**
 * Remember, before sign-in, that the page was opened from an email. Called from the sign-in screen, the one place that
 * is shown ONLY to someone who is not signed in yet.
 *
 * ⚠️ WHY IT IS REMEMBERED AT ALL. Signing in is a redirect to Microsoft and back, and whether the way back restores this URL's
 * query string is the identity library's business and not something this app controls or tests. Somebody who clicks the link
 * in an email is very likely to have to sign in first, so relying on the URL alone would open the drawer for the person who is
 * already signed in and lose it for the one who is not.
 */
export function rememberFeedbackLink(search: string, store: Store | undefined = tabStorage()): void {
  if (hasFeedbackLink(search)) attempt(() => store?.setItem(STASH_KEY, '1'), undefined);
}

/**
 * Should the drawer open on My feedback right now? Yes if the link is in the URL, or was remembered before sign-in. Taking it
 * forgets the memory, so a later reload does not open the drawer again.
 */
export function takeFeedbackLink(search: string, store: Store | undefined = tabStorage()): boolean {
  const remembered = attempt(() => store?.getItem(STASH_KEY) === '1', false);
  if (remembered) attempt(() => store?.removeItem(STASH_KEY), undefined);
  return remembered || hasFeedbackLink(search);
}

/** The query string without the link, so a reload does not open the drawer again. Other parameters are left alone. */
export function withoutFeedbackLink(search: string): string {
  const params = new URLSearchParams(search);
  params.delete(FEEDBACK_LINK_PARAM);
  const rest = params.toString();
  return rest ? `?${rest}` : '';
}

// ─── What the screen shows ──────────────────────────────────────────────────────

/** How many of my items have something I have not read. The number on the header button. */
export const unreadCount = (items: readonly Pick<MyFeedbackItem, 'unread'>[] | undefined): number =>
  items ? items.filter((i) => i.unread).length : 0;

/**
 * An item's state in the words people use. A close with no reason GitHub gives us, or as a duplicate, is neither "done" nor
 * "won't do", so it says only that it is closed, as the email for it does.
 */
export function statusLabel(status: string, reason: FeedbackCloseReason | string | null | undefined): string {
  if (status !== 'closed') return 'Open';
  if (reason === 'completed') return 'Done';
  if (reason === 'not_planned') return "Won't do";
  return 'Closed';
}

/**
 * An item in My feedback, in the words people use. ⚠️ AN ITEM AN ADMIN COMBINED INTO ANOTHER READS "Combined", NOT "Closed": it was closed as a duplicate,
 * and "Closed" alone reads as "no" to the person who asked for it. One that has been reopened since reads as what its status says.
 */
export function myStatusLabel(i: Pick<MyFeedbackItem, 'status' | 'closedReason' | 'combinedInto'>): string {
  if (i.status === 'closed' && i.combinedInto != null) return 'Combined';
  return statusLabel(i.status, i.closedReason);
}

// The query keys of the feedback screens, written once. The drawer reads them. The Admin tab refreshes MINE_KEY after a reply or a close, and
// both after a Show or a Hide; a literal copy on either side would let a rename quietly stop one of those refreshes.

/** The items I follow: the button's number, and the My feedback tab. */
export const MINE_KEY = ['feedback', 'mine'] as const;
/** What everyone may read: the Everyone's feedback tab, and the suggestions. */
export const BOARD_KEY = ['feedback', 'board'] as const;

/** The first non-blank line of a message, cut to `max` characters with an ellipsis, for a list row. */
export function firstLine(message: string, max = 90): string {
  const line = message.split(/\r?\n/).map((l) => l.trim()).find(Boolean) ?? '';
  const chars = Array.from(line.replace(/\s+/g, ' '));
  return chars.length > max ? `${chars.slice(0, max - 1).join('').trimEnd()}…` : chars.join('');
}

/** The words people use for a category, in My feedback and in the admin tab and its dialogs. */
export const CATEGORY_LABEL: Record<string, string> = { bug: 'Bug', idea: 'Idea', question: 'Question' };

/**
 * "Oct 16, 2:05 PM", the way the rest of the app writes a moment, and with the YEAR when it is not this year: feedback lists hold open
 * requests of any age, and "Oct 16" would read as this month for one that has been waiting since last year. A malformed date is a dash,
 * never the words "Invalid Date". `now` is for the specs.
 */
export const fmtWhen = (iso: string | Date, now: Date = new Date()): string => {
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return '—';
  return (when.getFullYear() === now.getFullYear() ? THIS_YEAR : OTHER_YEAR).format(when);
};
// Built once: a list of up to 200 rows formats a date or two for each, on every render, and a formatter is dear to make.
const FORMAT = { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' } as const;
const THIS_YEAR = new Intl.DateTimeFormat('en-US', FORMAT);
const OTHER_YEAR = new Intl.DateTimeFormat('en-US', { ...FORMAT, year: 'numeric' });
